import React, { useMemo } from 'react';
import { Button, Form } from 'reactstrap';
import { inject, observer } from 'mobx-react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';

import { createForm } from '../../../models/Form';
import globalTranslations from '../../../i18n/globalTranslations';
import FormGroupField from '../../form/FormGroupField';
import { allowEmpty, notBlank } from '../../../util/formValidators';
import { modelOf } from '../../../prop-types';
import { createFormField } from '../../../models/FormField';
import AccountStore from '../../../store/AccountStore';
import Address from '../../../models/Address';
import CheckboxField from '../../form/CheckboxField';
import CountryStore from '../../../store/CountryStore';
import SelectField from '../../form/SelectField';
import FormValidator from '../../../services/FormValidator';

const labels = {
  company: <FormattedMessage {...globalTranslations.companyTitle} />,
  firstname: <FormattedMessage {...globalTranslations.firstNameTitle} />,
  lastname: <FormattedMessage {...globalTranslations.lastNameTitle} />,
  postalCode: <FormattedMessage {...globalTranslations.zipCodeTitle} />,
  streetAddress: (
    <FormattedMessage {...globalTranslations.streetAddressTitle} />
  ),
  city: <FormattedMessage {...globalTranslations.cityTitle} />,
  telephone: <FormattedMessage {...globalTranslations.telephoneTitle} />,
  countryId: <FormattedMessage {...globalTranslations.countryTitle} />,
  setAsPrimary: (
    <FormattedMessage
      id="address.setAsPrimary"
      defaultMessage="Set as a primary address"
    />
  )
};

function createOption(value, label) {
  return {
    value,
    label
  };
}

const AddressForm = ({ loading, buttonText, address, onSubmit, accountStore, countryStore, formValidator }) => {
  const formName = 'AddressForm';

  const form = useMemo(() => {
    return createForm({
      company: createFormField(
        { value: address && address.company },
        accountStore.isRetailer ? [notBlank(labels.company)] : []
      ),
      firstname: createFormField(
        { value: address && address.firstname },
        !accountStore.isRetailer
          ? formValidator.firstName(labels.firstname)
          : formValidator.firstName(labels.firstname).map(allowEmpty)
      ),
      lastname: createFormField(
        { value: address && address.lastname },
        !accountStore.isRetailer
          ? formValidator.lastName(labels.firstname)
          : formValidator.lastName(labels.firstname).map(allowEmpty)
      ),
      streetAddress: createFormField(
        { value: address && address.street_address },
        formValidator.streetAddress(labels.streetAddress)
      ),
      postalCode: createFormField(
        { value: address && address.postal_code },
        formValidator.postalCode(labels.postalCode)
      ),
      city: createFormField(
        { value: address && address.city },
        formValidator.city(labels.city)
      ),
      countryId: createFormField({
        value: address ? address.country_id : accountStore.account.country_id
      }),
      telephone: createFormField(
        { value: address && address.telephone },
        formValidator.telephone(labels.telephone).map(allowEmpty)
      ),
      setAsPrimary: createFormField({
        value: address && address.is_primary ? '1' : ''
      })
    });
  }, [address, accountStore, formValidator]);

  const handleSubmit = (e) => {
    e.preventDefault();
    form.validate();
    if (form.valid) {
      onSubmit(form.getFieldValues());
    }
  };

  const renderField = (fieldName, props) => {
    const field = form.fields.get(fieldName);
    return (
      field.active && (
        <FormGroupField
          field={field}
          formName={formName}
          fieldName={fieldName}
          label={labels[fieldName]}
          {...props}
        />
      )
    );
  };

  const renderCheckbox = (fieldName, props) => {
    const field = form.fields.get(fieldName);
    return (
      field.active && (
        <CheckboxField
          field={field}
          formName={formName}
          fieldName={fieldName}
          {...props}
        />
      )
    );
  };

  const renderCountrySelect = () => {
    if (countryStore.countries.length <= 1) {
      return null;
    }

    const fieldName = 'countryId';
    const field = form.fields.get(fieldName);

    return (
      field.active && (
        <SelectField
          field={field}
          formName={formName}
          fieldName={fieldName}
          label={labels[fieldName]}
          options={countryStore.countries.map((country) =>
            createOption(String(country.id), country.name)
          )}
        />
      )
    );
  };

  // Don't show setAsPrimary checkbox if the address is already primary
  const showSetAsPrimary = !(address && address.is_primary);

  return (
    <Form className={formName} noValidate onSubmit={handleSubmit}>
      {renderField('firstname', {
        autoComplete: 'given-name',
        required: !accountStore.isRetailer
      })}
      {renderField('lastname', {
        autoComplete: 'family-name',
        required: !accountStore.isRetailer
      })}
      {renderField('company', {
        autoComplete: 'organization',
        required: accountStore.isRetailer
      })}
      {renderField('streetAddress', {
        autoComplete: 'street-address',
        required: true
      })}
      {renderField('postalCode', {
        autoComplete: 'postal-code',
        required: true
      })}
      {renderField('city', { autoComplete: 'address-level2', required: true })}
      {renderCountrySelect()}
      {renderField('telephone', { type: 'tel', autoComplete: 'tel-national' })}
      {showSetAsPrimary && renderCheckbox('setAsPrimary', {
        options: [createOption('1', labels.setAsPrimary)]
      })}
      <Button color="primary" disabled={form.valid === false || loading}>
        {buttonText}
      </Button>
    </Form>
  );
};

AddressForm.propTypes = {
  accountStore: modelOf(AccountStore).isRequired,
  countryStore: modelOf(CountryStore).isRequired,
  formValidator: PropTypes.instanceOf(FormValidator).isRequired,
  onSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  buttonText: PropTypes.node.isRequired,
  address: modelOf(Address),
};

export default inject('accountStore', 'countryStore', 'formValidator')(
  observer(AddressForm)
);
